import { FC, useEffect } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectStores, setAnswer } from '@/store/reducers/survey/surveySlice';
import { ReactComponent as Check } from '@/assets/images/check.svg';
import { SHOPS } from '@/constants/shops';
import useSurveyNavigate from '@/hooks/useSurveyNavigate';
import SurveyDefaultStep from '@/components/SurveyDefaultStep';
import analytics from '@/utils/analytics/analyticsInstance';

const Main = styled(Box)(() => ({
  gap: 4,
  padding: 16,
  paddingBottom: 96,
  display: 'flex',
  flexDirection: 'column',
}));

const Item = styled('button')<{ active: boolean }>(({ theme, active }) => {
  const borderColor = theme.palette.border.contrastText;

  return {
    gap: 12,
    opacity: 0,
    paddingInline: 16,
    paddingBlock: 12,
    borderWidth: 1,
    outlineWidth: 1,
    borderRadius: 12,
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    borderStyle: 'solid',
    outlineStyle: 'solid',
    background: 'transparent',
    transform: 'scale(1)',
    animationName: 'fadeInScale',
    animationDuration: '200ms',
    animationFillMode: 'forwards',
    animationTimingFunction: 'ease-out',
    transition: 'border-color 0.2s ease, outline-color 0.2s ease',
    borderColor: active ? borderColor : theme.palette.border.main,
    outlineColor: active ? borderColor : 'transparent',

    '@media (any-hover: hover)': {
      '&:hover': {
        borderColor: borderColor,
        outlineColor: borderColor,
      },
    },
  };
});

const Image = styled('img')(() => ({
  width: 32,
  height: 32,
}));

const Icon = styled(Check)(() => ({
  marginLeft: 'auto',
}));

const FavoriteStoresPage: FC = () => {
  const { next } = useSurveyNavigate();
  const dispatch = useAppDispatch();
  const selectedShops = useAppSelector(selectStores);
  const question = 'What stores do you love to shop at?';

  const handleSubmit = () => {
    analytics.track('webOnboardingQuestionAnswered', {
      question,
      answer: selectedShops,
    });
    next();
  };

  useEffect(() => {
    analytics.track('webOnboardingScreenOpened', {
      screen_title: question,
    });
  }, []);

  return (
    <SurveyDefaultStep
      title={{ value: question, lineHeight: '32px', fontSize: 28 }}
      subTitle={{ value: 'Pick the places you shop most often.' }}
      button={{ onClick: handleSubmit, disabled: !selectedShops.length }}
      footer={{
        position: 'fixed',
        sx: {
          bottom: -1,
          left: -1,
          right: -1,
          padding: 0,
          height: 81,
          paddingInline: '17px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'white',
        },
      }}
    >
      <Main>
        {SHOPS.map(({ image, name }, index) => (
          <Item
            key={index}
            onClick={() => dispatch(setAnswer({ key: 'stores', value: name }))}
            active={selectedShops.includes(name)}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <Image src={image} />
            <Typography fontSize={20} fontWeight={700} color="black">
              {name}
            </Typography>
            {selectedShops.includes(name) && <Icon />}
          </Item>
        ))}
      </Main>
    </SurveyDefaultStep>
  );
};

export default FavoriteStoresPage;
